import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('steel_token');
    localStorage.removeItem('steel_user');
    setUser(null);
  };

  useEffect(() => {
    const token = localStorage.getItem('steel_token');
    const savedUser = localStorage.getItem('steel_user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      authAPI.getProfile()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('steel_user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const loginWithOTP = async (phone, otp, userData = {}) => {
    const response = await authAPI.verifyOTP({ 
      phone, 
      otp,
      name: userData.name,
      email: userData.email,
      company_name: userData.company_name,
      city: userData.city
    });
    const { access_token, user: userInfo, is_new_user } = response.data;
    
    localStorage.setItem('steel_token', access_token);
    localStorage.setItem('steel_user', JSON.stringify(userInfo));
    setUser(userInfo);
    
    return { user: userInfo, is_new_user };
  };

  // MSG91 Widget OTP verification
  const loginWithMSG91 = async (accessToken, phone, userData = {}) => {
    const response = await authAPI.verifyMSG91Token({
      access_token: accessToken,
      phone: phone,
      name: userData.name,
      email: userData.email,
      company_name: userData.company_name,
      city: userData.city
    });
    const { access_token: jwtToken, user: userInfo, is_new_user } = response.data;
    
    localStorage.setItem('steel_token', jwtToken);
    localStorage.setItem('steel_user', JSON.stringify(userInfo));
    setUser(userInfo);
    
    return { user: userInfo, is_new_user };
  };

  // Temporary direct login (bypasses OTP while DLT registration is pending)
  const loginDirect = async (phone, userData = {}) => {
    const response = await authAPI.loginDirect({ 
      phone,
      name: userData.name,
      email: userData.email,
      company_name: userData.company_name,
      city: userData.city
    });
    const { access_token, user: userInfo, is_new_user } = response.data;
    
    localStorage.setItem('steel_token', access_token);
    localStorage.setItem('steel_user', JSON.stringify(userInfo));
    setUser(userInfo);
    
    return { user: userInfo, is_new_user };
  };

  // Update user profile
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('steel_user', JSON.stringify(updatedUser));
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      loginWithOTP,
      loginWithMSG91,
      loginDirect,
      updateUser,
      logout,
      isAdmin,
      isAuthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
